import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';

import { ReportsService } from '../../../domain/services/reports/reports.service';
import { DistributionModel, DistributionStatus } from '../../../domain/models/distribution.model';
import { ReportsNavComponent } from '../reports-nav.component';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-reports-list',
  standalone: true,
  imports: [
    ReportsNavComponent,
    MatCardModule,
    CommonModule,
    CurrencyPipe,
    DatePipe,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOption,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatChipsModule,
  ],
  templateUrl: './reports-list.component.html',
  styleUrl: './reports-list.component.css',
})
export class ReportsListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // MatTableDataSource: the hand-rolled one threw "set the paginator and sort before connecting"
  // when the table rendered first, so the list never showed any run (QA 2026-10-06).
  dataSource = new MatTableDataSource<DistributionModel>([]);
  displayedColumns = ['period', 'grossCollected', 'status', 'actions'];

  allDistributions: DistributionModel[] = [];

  // ponytail: latest-N cap so the page isn't an unbounded list; bump if a county needs deeper history.
  readonly maxRows = 20;

  filterType = 'Distribution';
  filterMonth = '';
  filterStatus = '';
  filterLabel = '';

  readonly DistributionStatus = DistributionStatus;

  readonly months = [
    { value: '', label: 'All' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  readonly statusOptions = [
    { value: '', label: 'All' },
    { value: DistributionStatus.DRAFT, label: 'Draft' },
    { value: DistributionStatus.READY, label: 'Ready' },
    { value: DistributionStatus.APPROVED, label: 'Approved' },
    { value: DistributionStatus.FINALIZED, label: 'Finalized' },
  ];

  constructor(private readonly reportsService: ReportsService) {}

  ngOnInit(): void {
    this.loadDistributions();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  loadDistributions(): void {
    this.reportsService.getDistributions().subscribe((data) => {
      this.allDistributions = data;
      this.applyFiltersAndRebuild();
    });
  }

  onSearch(): void {
    this.paginator?.firstPage(); // before rebuilding: a reset after would leave a stale (empty) page
    this.applyFiltersAndRebuild();
  }

  applyFiltersAndRebuild(): void {
    let filtered = [...this.allDistributions];

    if (this.filterMonth) {
      filtered = filtered.filter((d) => d.periodStart.substring(5, 7) === this.filterMonth);
    }

    if (this.filterStatus) {
      filtered = filtered.filter((d) => d.status === this.filterStatus);
    }

    const label = this.filterLabel.trim().toLowerCase();
    if (label) {
      filtered = filtered.filter((d) => (d.label ?? '').toLowerCase().includes(label));
    }

    // Default view: newest distributions first, capped to the latest N.
    filtered.sort((a, b) => b.periodStart.localeCompare(a.periodStart));
    filtered = filtered.slice(0, this.maxRows);

    this.rebuildDataSource(filtered);
  }

  rebuildDataSource(data: DistributionModel[]): void {
    this.dataSource.data = data;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  get activeFilters(): string[] {
    const filters: string[] = [];
    if (this.filterMonth) {
      const month = this.months.find((m) => m.value === this.filterMonth);
      if (month) filters.push(`Month: ${month.label}`);
    }
    if (this.filterStatus) {
      const status = this.statusOptions.find((s) => s.value === this.filterStatus);
      if (status) filters.push(`Status: ${status.label}`);
    }
    if (this.filterLabel.trim()) {
      filters.push(`Label: ${this.filterLabel.trim()}`);
    }
    return filters;
  }

  removeFilter(filter: string): void {
    if (filter.startsWith('Month:')) {
      this.filterMonth = '';
    } else if (filter.startsWith('Label:')) {
      this.filterLabel = '';
    } else if (filter.startsWith('Status:')) {
      this.filterStatus = '';
    }
    this.onSearch();
  }

  formatPeriod(periodStart: string, periodEnd: string): string {
    const fmt = (d: string) => {
      const [y, m, day] = d.split('-');
      return `${m}/${day}/${y}`;
    };
    return `${fmt(periodStart)} - ${fmt(periodEnd)}`;
  }

  getStatusLabel(status: DistributionStatus): string {
    switch (status) {
      case DistributionStatus.DRAFT:
        return 'Draft';
      case DistributionStatus.READY:
        return 'Ready';
      case DistributionStatus.APPROVED:
        return 'Approved';
      case DistributionStatus.FINALIZED:
        return 'Finalized';
      case DistributionStatus.VOID:
        return 'Void';
      default:
        return status;
    }
  }

  onRerun(event: Event, id: number): void {
    event.stopPropagation();
    this.reportsService.rerunDistribution(id).subscribe(() => {
      this.loadDistributions();
    });
  }

  onApprove(event: Event, id: number): void {
    event.stopPropagation();
    this.reportsService.approveDistribution(id).subscribe(() => {
      this.loadDistributions();
    });
  }
}
